import { Button } from '@components/Button';
import PropertyListingCard from '@components/Card/PropertyListingCard';
import PropertyCard from '@components/PropertiesSearch/PropertyCard';
import Block from '@components/Layout/Block';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import NullResultsNewestListings from '@components/NullResults/NewestListings';
import {
  LifestyleVerticalEnum,
  ListingSortKeys,
  SortDir,
  StandardStatusEnum,
} from '@engelvoelkersamericas/api';
import { apiClient } from 'api-client';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useEffect, useState } from 'react';
import CardGridWithMobileCarousel from '..';
import { CarouselCtaWrapper, CarouselHeaderWrapper, CarouselHeadline } from '../style';

export interface FeaturedPropertiesByLifestyleProps {
  header: string;
  lifestyle: string;
  localeDictionary: Record<string, string>;
  locale: string;
}

const FeaturedPropertiesByLifestyle: FC<FeaturedPropertiesByLifestyleProps> = ({
  header,
  lifestyle,
}) => {
  const t = useTranslations();
  const cardCount = 6;

  const placeholderCards = Array.from({ length: cardCount }, (_, i) => (
    <PropertyListingCard
      key={i.toString()}
      id={i.toString()}
      listOfficeMlsId={''}
      imgs={['/assets/textimageplaceholder.png']}
      listUrl="#"
      listPrice={0}
      closePrice={0}
      closePriceToDisplay={''}
      address={''}
      listingStatus={''}
      propertySubType={''}
      bathrooms={''}
      bedrooms={''}
      squareFootage={''}
      acreage={''}
      listOfficeName={''}
      listOfficeUrl="#"
      propertyType={''}
      originatingSystemName={''}
      currencyCode={''}
      country={''}
    />
  ));

  // set initial state for cardArr
  const [cardArr, setCardArr] = useState<ReactNode[]>(placeholderCards);

  let lifestyleArr: LifestyleVerticalEnum[] = [];
  switch (lifestyle) {
    case 'Water':
      lifestyleArr = [
        LifestyleVerticalEnum.WaterOcean,
        LifestyleVerticalEnum.WaterLake,
        LifestyleVerticalEnum.WaterRiver,
        LifestyleVerticalEnum.WaterView,
        LifestyleVerticalEnum.WaterAccess,
        LifestyleVerticalEnum.WaterWaterfront,
        LifestyleVerticalEnum.WaterMooring,
        LifestyleVerticalEnum.WaterDock,
      ];
      break;
    case 'Golf':
      lifestyleArr = [
        LifestyleVerticalEnum.GolfOnCourse,
        LifestyleVerticalEnum.GolfCommunity,
        LifestyleVerticalEnum.GolfLot,
      ];
      break;
    case 'Mountain':
      lifestyleArr = [
        LifestyleVerticalEnum.MountainMountain,
        LifestyleVerticalEnum.MountainWater,
        LifestyleVerticalEnum.MountainCity,
      ];
      break;
    case 'Wine':
      lifestyleArr = [
        LifestyleVerticalEnum.WineVineyardWine,
        LifestyleVerticalEnum.WineVineyardVineyard,
      ];
      break;
    case 'Ranch':
      lifestyleArr = [
        LifestyleVerticalEnum.RanchRecreationalRanchSportsTrails,
        LifestyleVerticalEnum.RanchRecreationalRanchHunting,
        LifestyleVerticalEnum.RanchRecreationalRanchFishing,
        LifestyleVerticalEnum.RanchRecreationalRanchOpenLand,
        LifestyleVerticalEnum.RanchRecreationalRanchRanchEstate,
        LifestyleVerticalEnum.RanchRecreationalRanchHospitalityDudeRanch,
        LifestyleVerticalEnum.RanchWorkingRanchLivestock,
        LifestyleVerticalEnum.RanchWorkingRanchAgriculture,
        LifestyleVerticalEnum.RanchWorkingRanchEquestrian,
      ];
      break;
    case 'Ski':
      lifestyleArr = [LifestyleVerticalEnum.SkiSkiInSkiOut];
      break;
    case 'Equestrian':
      lifestyleArr = [LifestyleVerticalEnum.Equestrian];
      break;
  }

  let category: string | undefined;
  switch (lifestyle) {
    case 'Water':
      category = `/properties/lifestyle/water`;
      break;
    case 'Golf':
      category = `/properties/lifestyle/golf`;
      break;
    case 'Mountain':
      category = `/properties/lifestyle/mountain`;
      break;
    case 'Wine':
      category = `/properties/lifestyle/wine`;
      break;
    case 'Ranch':
      category = `/properties/lifestyle/ranch`;
      break;
    case 'Ski':
      category = `/properties/lifestyle/ski`;
      break;
    case 'Equestrian':
      category = `/properties/lifestyle/equestrian`;
      break;
  }

  // Fetch data from the API and populate cardArr state
  const fetchAndSetCardArr = async () => {
    try {
      const data = await apiClient.getListings({
        page: 1,
        size: cardCount,
        sortKey: ListingSortKeys.UsdListPrice,
        sortDir: SortDir.Desc,
        listingFilters: {
          lifestyles: lifestyleArr,
          listingStatuses: [StandardStatusEnum.Active],
        },
      });

      const listings = data?.data?.listings;

      // map over cardArr and create array of ShopCard components
      let cardElements: ReactNode[] = [];
      if (data && listings?.length > 0) {
        // push cardArr into cardElements

        listings.forEach((card, ind) => {
          cardElements.push(<PropertyCard key={ind} listing={card} idx={ind} />);
        });
      }
      setCardArr(cardElements);
    } catch (error) {
      console.error(error);
      setCardArr([]);
    }
  };

  useEffect(() => {
    fetchAndSetCardArr();
  }, []);

  return (
    <Block>
      <CarouselHeaderWrapper>
        <CarouselHeadline>{header}</CarouselHeadline>
      </CarouselHeaderWrapper>

      <VerticalLineSeparator />
      {cardArr.length > 0 ? (
        <>
          <CardGridWithMobileCarousel
            dataTestId={`featured-properties-by-lifestyle-${lifestyle}`}
            cardType="property"
            cardArr={cardArr}
          />

          <CarouselCtaWrapper>
            <Button
              dataTestId="view-all-listings-btn"
              url={category}
              label={t('View All Listings')}
              variant="secondary"
              arrow={false}
            />
          </CarouselCtaWrapper>
        </>
      ) : (
        <NullResultsNewestListings
          header={t('noResultsContactShopOrAdvisor')}
          bodyText={t('noResultsContactAdvisors')}
        />
      )}
    </Block>
  );
};

export default FeaturedPropertiesByLifestyle;
